import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Package,
  FolderOpen,
  FileText,
  Image,
  Award,
  MessageSquare,
  ArrowRight,
  Clock,
  ExternalLink,
  Eye,
  Globe,
  Lock,
  Unlock,
  Home,
  Info,
  Phone,
  ShoppingBag,
  ImageIcon,
  ShieldCheck,
  ShoppingCart,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useMaintenanceMode } from '@/hooks/useSiteSettings'
import { cn } from '@/lib/utils'

export function DashboardPage() {
  const queryClient = useQueryClient()

  // Fetch maintenance mode
  const { data: maintenanceMode, isLoading: maintenanceLoading } = useMaintenanceMode()

  // Toggle maintenance mode mutation
  const toggleMaintenanceMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from('site_settings')
        .update({
          value: {
            enabled,
            message: 'We are launching soon!',
            allow_preview: true,
          },
        })
        .eq('key', 'maintenance_mode')

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-mode'] })
    },
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const [products, categories, quotes, gallery, certificates, messages] =
        await Promise.all([
          supabase.from('products').select('id', { count: 'exact', head: true }),
          supabase.from('categories').select('id', { count: 'exact', head: true }),
          supabase.from('quote_requests').select('id', { count: 'exact', head: true }),
          supabase.from('gallery_images').select('id', { count: 'exact', head: true }),
          supabase.from('certifications').select('id', { count: 'exact', head: true }),
          supabase.from('contact_messages').select('id', { count: 'exact', head: true }),
        ])

      return {
        products: products.count || 0,
        categories: categories.count || 0,
        quotes: quotes.count || 0,
        gallery: gallery.count || 0,
        certificates: certificates.count || 0,
        messages: messages.count || 0,
      }
    },
  })

  // Fetch recent quotes
  const { data: recentQuotes } = useQuery({
    queryKey: ['admin-recent-quotes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5)

      if (error) throw error
      return data
    },
  })

  const statCards = [
    { label: 'Products', value: stats?.products || 0, icon: Package, href: '/admin/products', color: 'bg-blue-500' },
    { label: 'Categories', value: stats?.categories || 0, icon: FolderOpen, href: '/admin/categories', color: 'bg-purple-500' },
    { label: 'Quote Requests', value: stats?.quotes || 0, icon: FileText, href: '/admin/quotes', color: 'bg-green-500' },
    { label: 'Gallery Images', value: stats?.gallery || 0, icon: Image, href: '/admin/gallery', color: 'bg-orange-500' },
    { label: 'Certificates', value: stats?.certificates || 0, icon: Award, href: '/admin/certificates', color: 'bg-red-500' },
    { label: 'Messages', value: stats?.messages || 0, icon: MessageSquare, href: '/admin/messages', color: 'bg-cyan-500' },
  ]

  const previewLinks = [
    { label: 'Home', href: '/?preview=true', icon: Home },
    { label: 'About', href: '/about?preview=true', icon: Info },
    { label: 'Contact', href: '/contact?preview=true', icon: Phone },
    { label: 'Products', href: '/products?preview=true', icon: ShoppingBag },
    { label: 'Gallery', href: '/gallery?preview=true', icon: ImageIcon },
    { label: 'Certificates', href: '/certificates?preview=true', icon: ShieldCheck },
    { label: 'Quote Basket', href: '/quote-basket?preview=true', icon: ShoppingCart },
  ]

  const isMaintenanceEnabled = maintenanceMode?.enabled ?? true

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Dashboard</h1>
        <p className="text-secondary-600">Welcome to Engimarine Admin Panel</p>
      </div>

      {/* Site Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card overflow-hidden"
      >
        <div className="p-6 bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Status Info */}
            <div className="flex items-center gap-4">
              <div className={cn(
                'w-14 h-14 rounded-2xl flex items-center justify-center',
                isMaintenanceEnabled ? 'bg-amber-500/20' : 'bg-green-500/20'
              )}>
                {isMaintenanceEnabled ? (
                  <Lock className="w-7 h-7 text-amber-400" />
                ) : (
                  <Globe className="w-7 h-7 text-green-400" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Site Status</h2>
                <p className={cn(
                  'text-sm font-medium',
                  isMaintenanceEnabled ? 'text-amber-400' : 'text-green-400'
                )}>
                  {isMaintenanceEnabled ? 'Coming Soon Mode Active' : 'Site is Live'}
                </p>
              </div>
            </div>

            {/* Toggle Button */}
            <button
              onClick={() => toggleMaintenanceMutation.mutate(!isMaintenanceEnabled)}
              disabled={maintenanceLoading || toggleMaintenanceMutation.isPending}
              className={cn(
                'flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all',
                isMaintenanceEnabled
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-amber-500 hover:bg-amber-600 text-white',
                (maintenanceLoading || toggleMaintenanceMutation.isPending) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {toggleMaintenanceMutation.isPending ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : isMaintenanceEnabled ? (
                <Unlock className="w-5 h-5" />
              ) : (
                <Lock className="w-5 h-5" />
              )}
              {isMaintenanceEnabled ? 'Launch Site' : 'Enable Coming Soon'}
            </button>
          </div>
        </div>

        {/* Preview Links */}
        <div className="p-6 bg-secondary-50 border-t border-secondary-100">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-secondary-500" />
            <h3 className="font-semibold text-secondary-900">Preview Pages</h3>
            <span className="text-xs text-secondary-500 bg-secondary-200 px-2 py-0.5 rounded-full">
              ?preview=true
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {previewLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-700 hover:border-primary-500 hover:text-primary-600 transition-all group"
              >
                <link.icon className="w-4 h-4" />
                {link.label}
                <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100" />
              </a>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link
              to={stat.href}
              className="block card p-6 hover:shadow-lg transition-shadow group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-500">{stat.label}</p>
                  <p className="text-3xl font-bold text-secondary-900 mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center text-white`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-primary-600 font-medium group-hover:gap-2 gap-1 transition-all">
                View all
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Recent Quote Requests */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
      >
        <div className="p-6 border-b border-secondary-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-secondary-900">
              Recent Quote Requests
            </h2>
            <Link
              to="/admin/quotes"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View all
            </Link>
          </div>
        </div>

        {recentQuotes && recentQuotes.length > 0 ? (
          <div className="divide-y divide-secondary-100">
            {recentQuotes.map((quote) => (
              <div
                key={quote.id}
                className="p-4 flex items-center justify-between hover:bg-secondary-50"
              >
                <div>
                  <p className="font-medium text-secondary-900">
                    {quote.company_name}
                  </p>
                  <p className="text-sm text-secondary-500">{quote.contact_name}</p>
                </div>
                <div className="text-right">
                  <span
                    className={`badge ${
                      quote.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : quote.status === 'contacted'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {quote.status}
                  </span>
                  <p className="text-xs text-secondary-400 mt-1 flex items-center justify-end gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(quote.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-secondary-500">
            No quote requests yet
          </div>
        )}
      </motion.div>
    </div>
  )
}
